import { useEffect, useState } from "react";
import { ApiError, sourcesApi, type GovernedSource } from "../../services/api";
import { createLogger, errorFields } from "../../services/clientLog";
import { selectionFor } from "../../services/dataDomain";
import type { SelectedContext } from "../../services/storage";

const log = createLogger("canvas_sources");

export interface QueryableSources {
  /** Null while loading. Only sources that can actually be queried. */
  sources: GovernedSource[] | null;
  error: string;
  /** Loaded, and nothing can be queried. */
  noSources: boolean;
  choose: (contextId: string) => void;
}

/**
 * The semantic models a canvas can ask about.
 *
 * Falls back to the first queryable model so a canvas is never unusable, but
 * never overrides a model the user already chose elsewhere in the app.
 */
export function useQueryableSources(
  selectedContext: SelectedContext | null,
  onSelectContext: (context: SelectedContext) => void,
): QueryableSources {
  const [sources, setSources] = useState<GovernedSource[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    sourcesApi
      .list()
      .then((items) => {
        if (active) setSources(items.filter((item) => item.queryable));
      })
      .catch((caught: unknown) => {
        if (!active) return;
        log("error", "load_failed", errorFields(caught));
        setError(
          caught instanceof ApiError ? caught.message : "Semantic models could not be loaded.",
        );
        setSources([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const first = sources?.[0];
    if (!sources || !first) return;
    const current = sources.some((item) => item.id === selectedContext?.contextId);
    if (!current) onSelectContext(selectionFor(first));
  }, [onSelectContext, selectedContext?.contextId, sources]);

  function choose(contextId: string) {
    const next = sources?.find((item) => item.id === contextId);
    if (next) onSelectContext(selectionFor(next));
  }

  return {
    sources,
    error,
    noSources: sources !== null && sources.length === 0,
    choose,
  };
}
